import * as React from "react";
import { Box } from "../../components/ui/box";
import { PALETTE_COLORS, type Color } from "../../lib/colors";
import type { AccentColor } from "../../lib/theme-appearance";
import { cn } from "../../lib/utils";
import type { Omittable } from "../../lib/props";

// Every value is a literal — Tailwind's scanner cannot see composed names.
const SWATCH_SIZE = {
  xs: "size-2",
  sm: "size-3",
  md: "size-4",
  lg: "size-5",
  xl: "size-6",
} as const;
export type ColorSwatchSize = keyof typeof SWATCH_SIZE;

const SWATCH_ROUNDED = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  full: "rounded-full",
} as const;
export type ColorSwatchRounded = keyof typeof SWATCH_ROUNDED;

type ColorSwatchSource =
  | {
      /** Palette or semantic color token. */
      color: Color;
      accent?: never;
    }
  | {
      color?: never;
      /**
       * An accent by name, painted from that accent's own `--primary` — which
       * is how a picker shows a theme it has not applied. `"none"` is the
       * shipped primary, so "no accent" is a swatch like any other rather than
       * a blank.
       *
       * This is a second source, not a second component: the box, the sizes and
       * the selection ring are the same, only where the fill comes from differs.
       */
      accent: AccentColor | "none";
    };

export type ColorSwatchProps = ColorSwatchSource &
  Omittable<{
    size?: ColorSwatchSize;
    rounded?: ColorSwatchRounded;
    /** Draw a selection ring around the swatch. */
    selected?: boolean;
    /** Click handler — renders the swatch as a real button when provided. */
    onClick?: () => void;
    "aria-label"?: string;
    className?: string;
  }>;

/**
 * A colored square or dot. Status markers, category indicators, and the
 * per-color cells of `ColorPalette`.
 */
export const ColorSwatch = React.memo(function ColorSwatch({
  color,
  accent,
  size = "md",
  rounded = "sm",
  selected,
  onClick,
  className,
  ...props
}: ColorSwatchProps) {
  const classes = cn(
    SWATCH_SIZE[size],
    SWATCH_ROUNDED[rounded],
    // `bg-primary` under a `data-accent` reads that accent's own block, which
    // is written unscoped for exactly this: a swatch paints a theme the
    // document has not adopted.
    accent === undefined ? "bg-c" : "bg-primary",
    selected && "ring-2 ring-ring ring-offset-2 ring-offset-background",
    className,
  );

  if (onClick) {
    return (
      <Box
        as="button"
        className={classes}
        data-color={color}
        data-accent={accent}
        data-slot="color-swatch"
        onClick={onClick}
        {...props}
      />
    );
  }

  return (
    <Box
      className={classes}
      data-color={color}
      data-accent={accent}
      data-slot="color-swatch"
      {...props}
    />
  );
});

/** Only the palette half of the scale — semantic roles are not pickable. */
export type ColorPaletteProps = {
  /** Currently selected color. */
  color: Color;
  /** Called when a swatch is clicked. */
  onColorChange: (color: Color) => void;
};

/** The full palette as a wrapped grid of pickable round swatches. */
export const ColorPalette = React.memo(function ColorPalette({
  color,
  onColorChange,
}: ColorPaletteProps) {
  return (
    <Box direction="row" wrap gap="xs" data-slot="color-palette">
      {PALETTE_COLORS.map((c) => (
        <Box rounded="full"
          key={c}
          as="button"
          aria-label={c}
          aria-pressed={c === color}
          className={cn(
            "size-6 bg-c",
            c === color && "border-2 border-foreground",
          )}
          data-color={c}
          data-slot="color-palette-swatch"
          onClick={() => onColorChange(c)}
        />
      ))}
    </Box>
  );
});
